import React, { useState, useEffect } from 'react';
import { Card } from '../ui/card';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Save } from 'lucide-react';
import apiClient from '../../utils/api';
import { toast } from 'sonner';

// Two-input master: Daily Capacity (Sq. Cm/day) + Number of Separator Lines.
// Consumed by ProductionScheduleTab's PS Print to inject bold horizontal
// dividers at every multiple of the capacity.
const DailyCapacitySettings = () => {
  const [capacity, setCapacity] = useState('');
  const [numLines, setNumLines] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const numericOnly = (value) => value.replace(/[^0-9.]/g, '').replace(/(\..*)\./g, '$1');
  const intOnly = (value) => value.replace(/[^0-9]/g, '');

  useEffect(() => {
    const load = async () => {
      try {
        const res = await apiClient.get('/settings/daily-capacity');
        setCapacity(String(res.data.capacity ?? 0));
        setNumLines(String(res.data.num_lines ?? 2));
      } catch (err) {
        toast.error('Failed to load Daily Capacity settings');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const handleSave = async () => {
    const cap = parseFloat(capacity || '0');
    const n = parseInt(numLines || '0', 10);
    if (isNaN(cap) || cap < 0) { toast.error('Enter a valid Daily Capacity'); return; }
    if (isNaN(n) || n < 0) { toast.error('Enter a valid Number of Lines'); return; }
    setSaving(true);
    try {
      await apiClient.put('/settings/daily-capacity', { capacity: cap, num_lines: n });
      toast.success('Daily Capacity saved');
    } catch (err) {
      toast.error('Failed to save Daily Capacity');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card className="p-6 bg-white rounded-lg border border-slate-200 max-w-xl" data-testid="daily-capacity-card">
      <h3 className="text-lg font-primary font-semibold text-slate-900 mb-1">Daily Capacity</h3>
      <p className="text-xs text-slate-500 mb-5">
        In the Production Schedule PS Print, a bold black line is drawn below the order at every multiple
        of the daily capacity (Sq. Cm). Number of Lines controls how many such dividers are drawn.
      </p>

      {loading ? (
        <div className="text-sm text-slate-500">Loading…</div>
      ) : (
        <div className="space-y-4">
          <div>
            <Label htmlFor="dc-capacity" className="text-xs font-semibold text-slate-700">
              Daily Capacity (Sq. Cm)
            </Label>
            <Input
              id="dc-capacity"
              type="text"
              inputMode="decimal"
              value={capacity}
              onChange={(e) => setCapacity(numericOnly(e.target.value))}
              placeholder="e.g. 50000"
              className="h-9 mt-1 text-right"
              data-testid="daily-capacity-input"
            />
          </div>
          <div>
            <Label htmlFor="dc-lines" className="text-xs font-semibold text-slate-700">
              Number of Lines
            </Label>
            <Input
              id="dc-lines"
              type="text"
              inputMode="numeric"
              value={numLines}
              onChange={(e) => setNumLines(intOnly(e.target.value))}
              placeholder="e.g. 2"
              className="h-9 mt-1 text-right"
              data-testid="daily-capacity-num-lines-input"
            />
          </div>
          <div className="pt-2">
            <Button
              onClick={handleSave}
              disabled={saving}
              className="bg-slate-900 hover:bg-slate-800 text-white"
              data-testid="daily-capacity-save-btn"
            >
              <Save className="h-4 w-4 mr-2" /> {saving ? 'Saving…' : 'Save'}
            </Button>
          </div>
        </div>
      )}
    </Card>
  );
};

export default DailyCapacitySettings;
